<template>
  <span ref="elementRef" :class="className" />
</template>

<script>
export default {
  name: 'NumberScroll',
  props: {
    to: {
      type: Number,
      required: true // 滚动目标数字（必传）
    },
    from: {
      type: Number,
      default: 0 // 滚动起始数字
    },
    direction: {
      type: String,
      default: 'up',
      validator: val => ['up', 'down'].includes(val) // 仅允许'up'/'down'两个方向
    },
    delay: {
      type: Number,
      default: 0 // 动画延迟时间（单位：秒）
    },
    duration: {
      type: Number,
      default: 2 // 动画持续时间（单位：秒）
    },
    className: {
      type: String,
      default: '' // 自定义类名
    },
    startWhen: {
      type: Boolean,
      default: true // 是否允许动画启动的开关
    },
    separator: {
      type: String,
      default: '' // 数字千分位分隔符（如',' '.'）
    },
    // 新增：小数点配置
    decimal: {
      type: Boolean,
      default: false // 是否开启小数点显示
    },
    decimalDigits: {
      type: Number,
      default: 2,
      validator: val => val >= 0 && val <= 8 // 小数位数限制：0-8位
    },
    onStart: {
      type: Function,
      default: null // 动画开始时的回调
    },
    onEnd: {
      type: Function,
      default: null // 动画结束时的回调
    }
  },
  data() {
    return {
      currentValue: this.direction === 'down' ? this.to : this.from, // 当前显示的数字
      isInView: false, // 元素是否在视口中
      animationId: null, // 动画帧ID，用于取消动画
      hasStarted: false, // 动画是否已启动
      intersectionObserver: null, // 视口检测实例
      velocity: 0, // 弹簧动画的速度
      startTime: 0, // 动画开始的时间戳
      maxFrames: 300, // 最多执行300帧（约5秒@60fps）
      frameCount: 0 // 当前执行帧数
    };
  },
  computed: {
    // 阻尼系数：控制动画衰减速度，与duration负相关
    damping() {
      return 20 + 40 * (1 / this.duration);
    },
    // 刚度系数：控制动画回弹强度，与duration负相关
    stiffness() {
      return 100 * (1 / this.duration);
    }
  },
  methods: {
    // 格式化数字：支持千分位分隔符 + 自定义小数位数
    formatNumber(value) {
      const options = {
        useGrouping: !!this.separator, // 是否启用千分位分组
        minimumFractionDigits: this.decimal ? this.decimalDigits : 0, // 最小小数位数
        maximumFractionDigits: this.decimal ? this.decimalDigits : 0 // 最大小数位数（与最小一致）
      };
      // 1. 按配置格式化数字（默认用英文格式，千分位为','）
      let formattedNumber = Intl.NumberFormat('en-US', options).format(value);
      // 2. 替换千分位分隔符（若用户自定义了separator）
      if (this.separator) {
        formattedNumber = formattedNumber.replace(/,/g, this.separator);
      }
      return formattedNumber;
    },
    // 更新DOM显示的数字
    updateDisplay() {
      if (this.$refs.elementRef) {
        this.$refs.elementRef.textContent = this.formatNumber(this.currentValue);
      }
    },
    // 弹簧物理动画核心逻辑（基于requestAnimationFrame）
    springAnimation(timestamp) {
      this.frameCount++;
      // 超过最大帧限制时强制终止（兜底逻辑）
      if (this.frameCount >= this.maxFrames) {
        const target = this.direction === 'down' ? this.from : this.to;
        this.currentValue = this.decimal 
          ? Number(target.toFixed(this.decimalDigits)) 
          : Math.round(target);
        this.updateDisplay();
        this.animationId = null;
        this.onEnd && this.onEnd();
        return;
      }

      if (!this.startTime) this.startTime = timestamp;

      // 根据方向确定动画目标值
      const target = this.direction === 'down' ? this.from : this.to;
      const current = this.currentValue;

      // 计算弹簧力、阻尼力和加速度
      const displacement = target - current;
      const springForce = displacement * this.stiffness;
      const dampingForce = this.velocity * this.damping;
      const acceleration = springForce - dampingForce;

      // 更新速度和当前值（假设60fps，时间间隔0.016秒）
      this.velocity += acceleration * 0.016;
      this.currentValue += this.velocity * 0.016;

      this.updateDisplay();

      // 动画终止条件：位移和速度足够小时停止
      const precision = this.decimal ? 0.001 : 0.01; // 小数场景用更高精度
      if (Math.abs(displacement) > precision || Math.abs(this.velocity) > precision) {
        this.animationId = requestAnimationFrame(this.springAnimation);
      } else {
        // 强制设置为目标值（保留对应小数位数），避免精度误差
        this.currentValue = this.decimal 
          ? Number(target.toFixed(this.decimalDigits)) 
          : Math.round(target);
        this.updateDisplay();
        this.animationId = null;

        // 触发结束回调
        if (this.onEnd) {
          this.onEnd();
        }
      }
    },
    // 启动动画（包含延迟逻辑）
    startAnimation() {
      // 避免重复启动：已启动/不在视口/开关关闭时不执行
      if (this.hasStarted || !this.isInView || !this.startWhen) return;

      this.hasStarted = true;
      // 触发开始回调
      if (this.onStart) {
        this.onStart();
      }

      // 延迟执行动画（单位转换：秒→毫秒）
      setTimeout(() => {
        this.startTime = 0;
        this.velocity = 0;
        this.frameCount = 0; // 重置帧计数
        this.animationId = requestAnimationFrame(this.springAnimation);
      }, this.delay * 1000);
    },
    // 初始化视口检测：元素进入视口时触发动画
    setupIntersectionObserver() {
      if (!this.$refs.elementRef) return;

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.isInView = true;
            this.startAnimation(); // 每次进入视口都尝试启动（内部会判断是否已启动）
          } else {
            this.isInView = false; // 离开视口时重置状态
          }
        },
        {
          threshold: 0,
          rootMargin: '0px',
          root: document.querySelector('.layout-content'),
        }
      );

      this.intersectionObserver.observe(this.$refs.elementRef);
    },
    // 清理资源：取消动画、断开视口检测
    cleanup() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }
  },
  watch: {
    // 监听from变化：重置当前值和动画状态，并触发动画
    from: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentValue = this.direction === 'down' ? this.to : newVal;
        this.updateDisplay();
        this.hasStarted = false;
        // 补充：如果元素在视口且允许启动，重新触发动画
        if (this.isInView && this.startWhen) {
          this.startAnimation();
        }
      }
    },
    // 监听to变化：重置当前值和动画状态，并触发动画
    to: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentValue = this.direction === 'down' ? newVal : this.from;
        this.updateDisplay();
        this.hasStarted = false;
        // 补充：如果元素在视口且允许启动，重新触发动画
        if (this.isInView && this.startWhen) {
          this.startAnimation();
        }
      }
    },
    // 监听direction变化：重置当前值和动画状态，并触发动画
    direction: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentValue = newVal === 'down' ? this.to : this.from;
        this.updateDisplay();
        this.hasStarted = false;
        if (this.isInView && this.startWhen) {
          this.startAnimation();
        }
      }
    },
    // 监听decimal变化：重置当前值和动画状态
    decimal: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentValue = this.direction === 'down' ? this.to : this.from;
        this.updateDisplay();
        this.hasStarted = false;
        if (this.isInView && this.startWhen) {
          this.startAnimation();
        }
      }
    },
    // 监听decimalDigits变化：重置当前值和动画状态
    decimalDigits: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentValue = this.direction === 'down' ? this.to : this.from;
        this.updateDisplay();
        this.hasStarted = false;
        if (this.isInView && this.startWhen) {
          this.startAnimation();
        }
      }
    },
    // 监听startWhen开关：打开时若满足条件则启动动画
    startWhen: function() {
      if (this.startWhen && this.isInView && !this.hasStarted) {
        this.startAnimation();
      }
    }
  },
  // 初始化时更新显示并设置视口检测
  mounted() {
    this.updateDisplay();
    this.setupIntersectionObserver();
  },
  // 销毁前清理资源，避免内存泄漏
  beforeDestroy() {
    this.cleanup();
  }
};
</script>